import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { GET_SYSTEM_SETTINGS, UPDATE_SYSTEM_SETTINGS, UPDATE_ORDER_SAMPLE_FILE, DELETE_ORDER_SAMPLE_FILE, UPDATE_FAVICON, DELETE_FAVICON } from '../apollo/queries/settingsQueries';
import { uploadFileToS3 } from '../utils/s3Upload';
import './css/AdminSettingsPage.css';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    maintenanceMode: false,
    desktopMaintenanceImage: '',
    mobileMaintenanceImage: '',
    faviconUrl: '',
    companyName: '',
    businessNumber: '',
    address: '',
    phone: '',
    fax: '',
    email: '',
    businessHours: '',
    businessDays: '',
    copyright: '',
    orderSampleFileName: '',
    orderSampleFileUrl: ''
  });
  
  const [desktopImage, setDesktopImage] = useState(null);
  const [mobileImage, setMobileImage] = useState(null);
  const [desktopImagePreview, setDesktopImagePreview] = useState('');
  const [mobileImagePreview, setMobileImagePreview] = useState('');
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState('');
  const [orderSampleFile, setOrderSampleFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data: settingsData, loading, error, refetch } = useQuery(GET_SYSTEM_SETTINGS);
  const [updateSystemSettings] = useMutation(UPDATE_SYSTEM_SETTINGS, {
    // 즉시 캐시 업데이트
    update: (cache, { data: { updateSystemSettings } }) => {
      if (updateSystemSettings.success && updateSystemSettings.settings) {
        try {
          cache.writeQuery({
            query: GET_SYSTEM_SETTINGS,
            data: {
              getSystemSettings: updateSystemSettings.settings
            }
          });
        } catch (error) {
          console.error('Cache update failed:', error);
        }
      }
    }
  });

  const [updateOrderSampleFile] = useMutation(UPDATE_ORDER_SAMPLE_FILE, {
    refetchQueries: [{ query: GET_SYSTEM_SETTINGS }]
  });

  const [deleteOrderSampleFile] = useMutation(DELETE_ORDER_SAMPLE_FILE, {
    refetchQueries: [{ query: GET_SYSTEM_SETTINGS }]
  });

  const [updateFavicon] = useMutation(UPDATE_FAVICON, {
    refetchQueries: [{ query: GET_SYSTEM_SETTINGS }]
  });

  const [deleteFavicon] = useMutation(DELETE_FAVICON, {
    refetchQueries: [{ query: GET_SYSTEM_SETTINGS }]
  });

  // 설정 데이터 로드 시 상태 업데이트
  useEffect(() => {
    if (settingsData?.getSystemSettings) {
      const settings = settingsData.getSystemSettings;
      console.log('Settings loaded:', settings);
      console.log('Desktop image URL:', settings.desktopMaintenanceImage);
      console.log('Mobile image URL:', settings.mobileMaintenanceImage);
      
      const serverData = {
        maintenanceMode: settings.maintenanceMode || false,
        desktopMaintenanceImage: settings.desktopMaintenanceImage || '',
        mobileMaintenanceImage: settings.mobileMaintenanceImage || '',
        faviconUrl: settings.faviconUrl || '',
        companyName: settings.companyName || '',
        businessNumber: settings.businessNumber || '',
        address: settings.address || '',
        phone: settings.phone || '',
        fax: settings.fax || '',
        email: settings.email || '',
        businessHours: settings.businessHours || '',
        businessDays: settings.businessDays || '',
        copyright: settings.copyright || '',
        orderSampleFileName: settings.orderSampleFileName || '',
        orderSampleFileUrl: settings.orderSampleFileUrl || ''
      };
      
      setFormData(serverData);
      
      if (settings.desktopMaintenanceImage) {
        console.log('Setting desktop image preview:', settings.desktopMaintenanceImage);
        setDesktopImagePreview(settings.desktopMaintenanceImage);
      }
      if (settings.mobileMaintenanceImage) {
        console.log('Setting mobile image preview:', settings.mobileMaintenanceImage);
        setMobileImagePreview(settings.mobileMaintenanceImage);
      }
      if (settings.faviconUrl) {
        console.log('Setting favicon preview:', settings.faviconUrl);
        setFaviconPreview(settings.faviconUrl);
      }
    }
  }, [settingsData]);

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDesktopImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDesktopImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setDesktopImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMobileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMobileImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setMobileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOrderSampleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOrderSampleFile(file);
    }
  };

  const handleOrderSampleFileUpload = async () => {
    if (!orderSampleFile) {
      alert('파일을 선택해주세요.');
      return;
    }

    // 파일 유효성 검사
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (orderSampleFile.size > maxSize) {
      alert(`파일 크기가 너무 큽니다. 최대 ${maxSize / (1024 * 1024)}MB까지 업로드 가능합니다.`);
      return;
    }

    if (!allowedTypes.includes(orderSampleFile.type)) {
      alert('지원하지 않는 파일 형식입니다. PDF, DOC, DOCX, XLS, XLSX 파일만 업로드 가능합니다.');
      return;
    }

    setUploading(true);
    try {
      const fileUrl = await uploadFileToS3(orderSampleFile);
      if (fileUrl) {
        const result = await updateOrderSampleFile({
          variables: {
            fileName: orderSampleFile.name,
            fileUrl: fileUrl
          }
        });

        if (result.data.updateOrderSampleFile.success) {
          alert('발주서 샘플 파일이 성공적으로 업로드되었습니다.');
          setOrderSampleFile(null);
          // 파일 입력 초기화
          const fileInput = document.getElementById('order-sample-file-input');
          if (fileInput) fileInput.value = '';
        } else {
          alert('파일 업로드에 실패했습니다: ' + result.data.updateOrderSampleFile.message);
        }
      } else {
        alert('파일 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error('발주서 샘플 파일 업로드 오류:', error);
      alert('파일 업로드 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleOrderSampleFileDelete = async () => {
    if (!formData.orderSampleFileUrl) {
      alert('삭제할 파일이 없습니다.');
      return;
    }

    if (!window.confirm('발주서 샘플 파일을 삭제하시겠습니까?')) {
      return;
    }

    setUploading(true);
    try {
      const result = await deleteOrderSampleFile();
      if (result.data.deleteOrderSampleFile.success) {
        alert('발주서 샘플 파일이 성공적으로 삭제되었습니다.');
      } else {
        alert('파일 삭제에 실패했습니다: ' + result.data.deleteOrderSampleFile.message);
      }
    } catch (error) {
      console.error('발주서 샘플 파일 삭제 오류:', error);
      alert('파일 삭제 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleOrderSampleFileDownload = () => {
    if (formData.orderSampleFileUrl) {
      const link = document.createElement('a');
      link.href = formData.orderSampleFileUrl;
      link.download = formData.orderSampleFileName || 'order_sample.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFaviconUpload = async () => {
    if (!faviconFile) {
      alert('파비콘 파일을 선택해주세요.');
      return;
    }

    // 파일 크기 체크 (1MB 이하)
    if (faviconFile.size > 1024 * 1024) {
      alert('파비콘 파일 크기는 1MB 이하여야 합니다.');
      return;
    }

    setUploading(true);
    try {
      const fileUrl = await uploadFileToS3(faviconFile);
      if (fileUrl) {
        const result = await updateFavicon({
          variables: {
            faviconUrl: fileUrl
          }
        });

        if (result.data.updateFavicon.success) {
          alert('파비콘이 성공적으로 업로드되었습니다.');
          setFaviconFile(null);
          // 파일 입력 초기화
          const fileInput = document.getElementById('favicon-file-input');
          if (fileInput) fileInput.value = '';
        } else {
          alert('파비콘 업로드에 실패했습니다: ' + result.data.updateFavicon.message);
        }
      } else {
        alert('파비콘 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error('파비콘 업로드 오류:', error);
      alert('파비콘 업로드 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconDelete = async () => {
    if (!formData.faviconUrl) {
      alert('삭제할 파비콘이 없습니다.');
      return;
    }

    if (!window.confirm('파비콘을 삭제하시겠습니까?')) {
      return;
    }

    setUploading(true);
    try {
      const result = await deleteFavicon();
      if (result.data.deleteFavicon.success) {
        alert('파비콘이 성공적으로 삭제되었습니다.');
        setFaviconPreview('');
      } else {
        alert('파비콘 삭제에 실패했습니다: ' + result.data.deleteFavicon.message);
      }
    } catch (error) {
      console.error('파비콘 삭제 오류:', error);
      alert('파비콘 삭제 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setSaveSuccess(false);

    try {
      let desktopImageUrl = formData.desktopMaintenanceImage;
      let mobileImageUrl = formData.mobileMaintenanceImage;
      let faviconUrl = formData.faviconUrl;

      // 새 이미지가 있으면 업로드
      if (desktopImage) {
        console.log('데스크톱 이미지 업로드 중...');
        try {
          desktopImageUrl = await uploadFileToS3(desktopImage);
          if (!desktopImageUrl) {
            // S3 업로드 실패 시 base64 데이터 URL 사용 (임시 방편)
            console.warn('S3 업로드 실패, base64 URL 사용');
            desktopImageUrl = desktopImagePreview; // 현재 프리뷰 이미지 사용
          }
          console.log('데스크톱 이미지 업로드 완료:', desktopImageUrl);
        } catch (error) {
          console.warn('S3 업로드 에러, base64 URL 사용:', error);
          desktopImageUrl = desktopImagePreview; // 현재 프리뷰 이미지 사용
        }
      }
      if (mobileImage) {
        console.log('모바일 이미지 업로드 중...');
        try {
          mobileImageUrl = await uploadFileToS3(mobileImage);
          if (!mobileImageUrl) {
            // S3 업로드 실패 시 base64 데이터 URL 사용 (임시 방편)
            console.warn('S3 업로드 실패, base64 URL 사용');
            mobileImageUrl = mobileImagePreview; // 현재 프리뷰 이미지 사용
          }
          console.log('모바일 이미지 업로드 완료:', mobileImageUrl);
        } catch (error) {
          console.warn('S3 업로드 에러, base64 URL 사용:', error);
          mobileImageUrl = mobileImagePreview; // 현재 프리뷰 이미지 사용
        }
      }
      if (faviconFile) {
        console.log('파비콘 업로드 중...');
        try {
          faviconUrl = await uploadFileToS3(faviconFile);
          if (!faviconUrl) {
            console.warn('파비콘 S3 업로드 실패, base64 URL 사용');
            faviconUrl = faviconPreview;
          }
          console.log('파비콘 업로드 완료:', faviconUrl);
        } catch (error) {
          console.warn('파비콘 S3 업로드 에러, base64 URL 사용:', error);
          faviconUrl = faviconPreview;
        }
      }

      const result = await updateSystemSettings({
        variables: {
          input: {
            ...formData,
            desktopMaintenanceImage: desktopImageUrl,
            mobileMaintenanceImage: mobileImageUrl,
            faviconUrl: faviconUrl
          }
        }
      });

      if (result.data.updateSystemSettings.success) {
        // 업로드된 이미지 프리뷰 업데이트
        if (desktopImageUrl && desktopImageUrl !== formData.desktopMaintenanceImage) {
          setDesktopImagePreview(desktopImageUrl);
        }
        if (mobileImageUrl && mobileImageUrl !== formData.mobileMaintenanceImage) {
          setMobileImagePreview(mobileImageUrl);
        }
        if (faviconUrl && faviconUrl !== formData.faviconUrl) {
          setFaviconPreview(faviconUrl);
        }
        
        // 폼 데이터 업데이트
        setFormData(prev => ({
          ...prev,
          desktopMaintenanceImage: desktopImageUrl,
          mobileMaintenanceImage: mobileImageUrl,
          faviconUrl: faviconUrl
        }));
        
        // 성공 메시지 표시
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        
        // 이미지 상태 초기화 (파일 입력만, 프리뷰는 유지)
        setDesktopImage(null);
        setMobileImage(null);
        setFaviconFile(null);
        
        alert('설정이 성공적으로 저장되었습니다!');
      } else {
        alert('설정 저장에 실패했습니다: ' + result.data.updateSystemSettings.message);
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      alert('설정 저장 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">설정을 불러오는 중...</div>;
  if (error) return <div className="error">오류가 발생했습니다: {error.message}</div>;

  return (
    <>
      <div className="breadcrumb">
        <span>홈 &gt; 환경 설정 &gt; 기본 환경 설정</span>
      </div>
      <div className="admin-settings-page">
        <div className="admin-settings-container">
          <aside className="admin-sidebar">
            <div className="sidebar-menu">
              <h3>환경 설정</h3>
              <ul>
                <li 
                  className={location.pathname === '/admin/settings' ? 'active' : ''}
                  onClick={() => handleMenuClick('/admin/settings')}
                >
                  기본 환경 설정
                </li>
                <li 
                  className={location.pathname === '/admin/profile' ? 'active' : ''}
                  onClick={() => handleMenuClick('/admin/profile')}
                >
                  관리자 정보수정
                </li>
              </ul>
            </div>
          </aside>

          <main className="admin-content">
            <div className="content-header">
              <h2>기본 환경 설정</h2>
              {saveSuccess && (
                <div className="save-success-message">
                  ✓ 설정이 성공적으로 저장되었습니다.
                </div>
              )}
            </div>

            <div className="settings-description">
              <p>푸터에 표시되는 회사 정보를 설정합니다. 여기서 입력한 정보는 사이트 하단 Footer에 표시됩니다.</p>
            </div>

            <form onSubmit={handleSubmit} className="settings-form">
              {/* 회사 기본 정보 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">회사 기본 정보</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>회사명</th>
                      <td>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="회사명을 입력하세요"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>사업자등록번호</th>
                      <td>
                        <input
                          type="text"
                          name="businessNumber"
                          value={formData.businessNumber}
                          onChange={handleInputChange}
                          className="input-medium"
                          placeholder="000-00-00000"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>주소</th>
                      <td>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="주소를 입력하세요"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>저작권 표시</th>
                      <td>
                        <input
                          type="text"
                          name="copyright"
                          value={formData.copyright}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="Copyrightⓒ 회사명. All right reserved."
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 연락처 정보 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">연락처 정보</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>전화번호</th>
                      <td>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="input-medium"
                          placeholder="031-479-1630"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>팩스번호</th>
                      <td>
                        <input
                          type="text"
                          name="fax"
                          value={formData.fax}
                          onChange={handleInputChange}
                          className="input-medium"
                          placeholder="031-000-0000"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>이메일</th>
                      <td>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="info@busbarworld.com"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 영업시간 정보 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">영업시간 정보</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>영업시간</th>
                      <td>
                        <input
                          type="text"
                          name="businessHours"
                          value={formData.businessHours}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="평일:오전9시~오후6시"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>휴무일</th>
                      <td>
                        <input
                          type="text"
                          name="businessDays"
                          value={formData.businessDays}
                          onChange={handleInputChange}
                          className="input-large"
                          placeholder="토요일/일요일/공휴일 휴무"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 시스템 설정 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">유지보수 설정</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>유지보수 모드</th>
                      <td>
                        <div className="maintenance-toggle-container">
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              id="maintenanceMode"
                              name="maintenanceMode"
                              checked={formData.maintenanceMode}
                              onChange={handleInputChange}
                              className="toggle-input"
                            />
                            <label htmlFor="maintenanceMode" className="toggle-label">
                              <span className="toggle-button"></span>
                            </label>
                          </div>
                          <span>{formData.maintenanceMode ? '활성화' : '비활성화'}</span>
                          <small style={{color: '#666', marginLeft: '10px'}}>
                            사이트 전체를 유지보수 모드로 전환합니다
                          </small>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>데스크톱 유지보수 이미지</th>
                      <td>
                        <div className="image-upload-container">
                          <div className="file-input-wrapper">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleDesktopImageChange}
                              className="file-input"
                              id="desktop-image-input"
                            />
                            <label htmlFor="desktop-image-input" className="file-input-label">
                              {desktopImage ? '다른 이미지 선택' : '이미지 선택'}
                            </label>
                          </div>
                          {desktopImagePreview && (
                            <div className="image-preview">
                              <p><strong>현재 이미지:</strong></p>
                              <img src={desktopImagePreview} alt="데스크톱 유지보수 이미지" />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>모바일 유지보수 이미지</th>
                      <td>
                        <div className="image-upload-container">
                          <div className="file-input-wrapper">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleMobileImageChange}
                              className="file-input"
                              id="mobile-image-input"
                            />
                            <label htmlFor="mobile-image-input" className="file-input-label">
                              {mobileImage ? '다른 이미지 선택' : '이미지 선택'}
                            </label>
                          </div>
                          {mobileImagePreview && (
                            <div className="image-preview">
                              <p><strong>현재 이미지:</strong></p>
                              <img src={mobileImagePreview} alt="모바일 유지보수 이미지" />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 파비콘 설정 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">파비콘 설정</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>파비콘 이미지</th>
                      <td>
                        <div className="favicon-management-container">
                          <div className="favicon-upload-section">
                            <div className="file-input-wrapper">
                              <input
                                type="file"
                                accept=".ico,.png,.jpg,.jpeg,.gif,.svg"
                                onChange={handleFaviconChange}
                                className="file-input"
                                id="favicon-file-input"
                              />
                              <label htmlFor="favicon-file-input" className="file-input-label">
                                {faviconFile ? '다른 파비콘 선택' : '파비콘 선택'}
                              </label>
                            </div>
                            {faviconFile && (
                              <button
                                type="button"
                                onClick={handleFaviconUpload}
                                className="upload-button"
                                disabled={uploading}
                              >
                                {uploading ? '업로드 중...' : '파비콘 업로드'}
                              </button>
                            )}
                          </div>
                          
                          {faviconPreview && (
                            <div className="favicon-preview-section">
                              <p><strong>현재 파비콘:</strong></p>
                              <div className="favicon-preview">
                                <img src={faviconPreview} alt="파비콘" style={{width: '32px', height: '32px'}} />
                                <button
                                  type="button"
                                  onClick={handleFaviconDelete}
                                  className="delete-button"
                                  disabled={uploading}
                                >
                                  삭제
                                </button>
                              </div>
                              <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                                권장 크기: 100 x 100 (PNG 형식 권장)
                              </small>
                            </div>
                          )}
                          
                          {!faviconPreview && !faviconFile && (
                            <div className="favicon-info">
                              <small style={{color: '#666'}}>
                                파비콘은 브라우저 탭에 표시되는 작은 아이콘입니다.<br/>
                                ICO, PNG, JPG, GIF, SVG 형식을 지원하며, 32x32 픽셀 크기를 권장합니다.
                              </small>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 발주서 샘플 파일 관리 섹션 */}
              <div className="settings-section">
                <h3 className="settings-section-title">발주서 샘플 파일 관리</h3>
                <table className="settings-form-table">
                  <tbody>
                    <tr>
                      <th>현재 파일</th>
                      <td>
                        {formData.orderSampleFileUrl ? (
                          <div className="current-file-info">
                            <div className="file-info">
                              <span className="file-name">
                                📄 {formData.orderSampleFileName || '발주서 샘플.pdf'}
                              </span>
                              <div className="file-actions">
                                <button 
                                  type="button" 
                                  className="btn-download"
                                  onClick={handleOrderSampleFileDownload}
                                >
                                  다운로드
                                </button>
                                <button 
                                  type="button" 
                                  className="btn-delete"
                                  onClick={handleOrderSampleFileDelete}
                                  disabled={uploading}
                                >
                                  삭제
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="no-file">등록된 파일이 없습니다.</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>파일 업로드</th>
                      <td>
                        <div className="file-upload-container">
                          <div className="file-input-wrapper">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx"
                              onChange={handleOrderSampleFileChange}
                              className="file-input"
                              id="order-sample-file-input"
                            />
                            <label htmlFor="order-sample-file-input" className="file-input-label">
                              {orderSampleFile ? orderSampleFile.name : '파일 선택'}
                            </label>
                          </div>
                          {orderSampleFile && (
                            <button 
                              type="button" 
                              className="btn-upload"
                              onClick={handleOrderSampleFileUpload}
                              disabled={uploading}
                            >
                              {uploading ? '업로드 중...' : '업로드'}
                            </button>
                          )}
                        </div>
                        <small className="file-help-text">
                          PDF, DOC, DOCX, XLS, XLSX 파일을 업로드할 수 있습니다. (최대 10MB)
                        </small>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="form-bottom-actions">
                <button 
                  type="submit" 
                  className="btn-save"
                  disabled={uploading}
                >
                  {uploading ? '저장 중...' : '설정 저장'}
                </button>
              </div>
            </form>
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminSettingsPage; 